/**
 * BBSA ontology v3.4.0 — the canonical hub skeleton as data (ADR-003 §2a, §8, §11).
 * Content anchored on the BBSA v2.0 spec (blackBoxIKAnimator/docs/
 * BLACKBOX_STANDARD_ARMATURE_SPEC.md): hierarchy, constraints, and T-pose.
 * 3.1.0 adds the accepted-proposal metadata: mirror plane + parity convention
 * (reflection proposal), zero-pose secondary axes and the clavicle
 * childDirectionInformative class (rest-pose reorientation proposal).
 * 3.2.0 adds restAnchoredTransport on the clavicle class (ADR-019 amendment,
 * chest-collapse forensics 2026-08-09): a clavicle's rest direction is
 * per-rig anatomy, so absolute transport anchors its rest to the TARGET's.
 * 3.4.0 adds sixteen OPTIONAL tail slots Tail1..Tail16 under the pelvis
 * (ADR-031 §3.3, D6; T2 ruled 2026-09-22): region 'tail', only ever GRAFTED
 * (no generator ships them), and EXCLUDED from every profile — `full` stays
 * the 72 hub bones, so every full-72 mapping keeps its coverage (Codex P2 on
 * the T2 note). A tail rides its own set, {@link TAIL_SLOT_IDS}.
 * 3.5.0 adds five OPTIONAL soft-tissue leaves, each with its tip (ADR-038
 * J2, the jiggle arc after tails): LeftBreastJiggle / RightBreastJiggle under
 * Spine2, BellyJiggle under Spine, LeftGluteJiggle / RightGluteJiggle under
 * Hips — region 'softTissue', only ever GRAFTED over a measured or declared
 * region, driven by a declared spring, and EXCLUDED from every profile as the
 * tails are. Distinct from the hub's `LeftBreast` (a `full`-profile slot a
 * retarget writes): a jiggle leaf is the spring's, and no clip track lands on
 * it. See {@link SOFT_TISSUE_SLOT_IDS}.
 * 3.6.0 adds ONE more soft-tissue leaf, HairJiggle under Head (ADR-039 T2a):
 * the stiff bounce leaf a confirmed ATTACHED hair region softens on — same
 * region, same exclusion from every profile, same grafted-only rule.
 * 3.3.0 declares the thumb OPPOSED (ADR-019 §2.2, rest-roll census
 * 2026-08-25): its zero-pose secondary is the in-plane radial perpendicular,
 * not a finger's dorsal UP — measured on Mixamo's own rig, the thumb MCP
 * flexes about the PALM NORMAL, so its nail faces radially — and the thumb
 * chain keeps its REST (restAnchoredTransport + anchorChildrenRide): the
 * receiver keeps its nail AND its modelled metacarpal — Mixamo's splayed
 * T-pose thumb moved a relaxed modelled knuckle 47–65 mm at source-rest.
 *
 * Transcription note (checked, not trusted — ADR-003 §9): the v2 spec's
 * summary table is internally inconsistent (claims 26 bones/arm and totals
 * 72 via numbers that sum to 77), but its hierarchy listing enumerates
 * EXACTLY 72 bones — 8 center + 32 per side — which this builder reproduces.
 * The "minimal" profile likewise enumerates to 25 (the spec's "27" doesn't
 * sum); profile IDs here deliberately carry no counts.
 *
 * The right side is generated by mirroring the left — mirror links and
 * direction reflection are correct by construction, then re-proven by the
 * loadOntology integrity checks and the self-test suite.
 */
import type { JointType, OntologyBone, OntologyData, SwingTwistLimits, Vec3 } from '../ontology.js';

function unit(x: number, y: number, z: number): Vec3 {
  const len = Math.hypot(x, y, z);
  return { x: x / len, y: y / len, z: z / len };
}

function limits(swingDeg: number, twistMinDeg: number, twistMaxDeg: number): SwingTwistLimits {
  return { swingDeg, twistMinDeg, twistMaxDeg };
}

interface BoneSeed {
  id: string;
  parent: string | null;
  region: OntologyBone['region'];
  jointType?: JointType;
  jointLimits?: SwingTwistLimits | null;
  tip?: boolean;
  dir?: Vec3 | null;
  /** Zero-pose twist/reference axis, ⊥ dir (ADR-003 §11). */
  sec?: Vec3 | null;
  /** False only for the clavicle class (and tips, which have no frame to read). */
  childInformative?: boolean;
  /** True only for the clavicle class: rest direction is per-rig ANATOMY, so
   *  absolute transport anchors source-rest to target-rest (ADR-019 amendment,
   *  chest-collapse forensics 2026-08-09). */
  restAnchored?: boolean;
  /** The narrower anchor: only the roll about the bone is kept, the swing
   *  still transports (ADR-019 §2.2). No BBSA bone declares it today — the
   *  thumb needed its whole rest — it stays for a rig class whose digit
   *  swing at rest IS pose. */
  twistAnchored?: boolean;
  /** Children ride the anchor instead of cancelling it: the thumb chain
   *  (a digit is one thing). The clavicle's arm cancels. */
  childrenRide?: boolean;
  /** The child zeroPoseDirection points at; null when childless (ADR-003 §11). */
  pc?: string | null;
}

function centerBone(seed: BoneSeed): OntologyBone {
  return {
    id: seed.id,
    parent: seed.parent,
    region: seed.region,
    side: 'center',
    mirror: null,
    joint: seed.jointType ? { type: seed.jointType, limits: seed.jointLimits ?? null } : null,
    tip: seed.tip ?? false,
    zeroPoseDirection: seed.tip ? null : (seed.dir ?? null),
    zeroPoseSecondary: seed.tip ? null : (seed.sec ?? null),
    parityAxis: null,
    childDirectionInformative: seed.tip ? false : (seed.childInformative ?? true),
    primaryChild: seed.tip ? null : (seed.pc ?? null),
    restAnchoredTransport: seed.tip ? false : (seed.restAnchored ?? false),
    twistAnchoredTransport: seed.tip ? false : (seed.twistAnchored ?? false),
    anchorChildrenRide: seed.tip ? false : (seed.childrenRide ?? false),
  };
}

function leftBone(seed: BoneSeed): OntologyBone {
  return {
    ...centerBone(seed),
    side: 'left',
    mirror: seed.id.replace(/^Left/, 'Right'),
    // The C in Rt = M·Rs·C: BBSA mirrors by pure sagittal reflection, so the
    // parity axis is uniformly +X — stated per bone, not implied (§11).
    parityAxis: LEFT_X,
  };
}

/** Reflect a left bone across the sagittal plane into its right twin. */
function mirrorToRight(left: OntologyBone): OntologyBone {
  const swap = (id: string): string => id.replace(/^Left/, 'Right');
  const swapRegion = (r: OntologyBone['region']): OntologyBone['region'] =>
    r.endsWith('L') ? ((r.slice(0, -1) + 'R') as OntologyBone['region']) : r;
  const reflectX = (v: Vec3 | null): Vec3 | null => (v === null ? null : { ...v, x: -v.x });
  return {
    ...left,
    id: swap(left.id),
    parent: left.parent === null ? null : swap(left.parent),
    region: swapRegion(left.region),
    side: 'right',
    mirror: left.id,
    zeroPoseDirection: reflectX(left.zeroPoseDirection),
    zeroPoseSecondary: reflectX(left.zeroPoseSecondary),
    primaryChild: left.primaryChild === null ? null : swap(left.primaryChild),
    // parityAxis is a reflection-plane normal in bone-local terms: n and −n
    // name the same plane, so the mirrored twin keeps the canonical +X.
  };
}

// ---- constraint table, straight from the BBSA v2.0 spec ----
const SPINE = limits(15, -20, 20);
const NECK = limits(30, -40, 40);
const HEAD = limits(25, -30, 30);
const JAW = limits(30, 0, 0);
const EYE = limits(30, 0, 0);
const BREAST = limits(20, -5, 5);
const CLAVICLE = limits(25, -15, 15);
const UPPER_ARM = limits(100, -90, 90);
const ELBOW = limits(145, -5, 5);
const UPPER_LEG = limits(110, -45, 45);
const KNEE = limits(140, -5, 5);
const FINGER_MCP = limits(90, -10, 10);
const THUMB_MCP = limits(60, -30, 30);
const FINGER_PIP = limits(100, 0, 0);
const FINGER_DIP = limits(70, 0, 0);
/**
 * A caudal joint: small, roughly ISOTROPIC bend and near-zero twist (memory
 * `tail-anatomy-priors`, ADR-031 §2) — no hinge anywhere. Per joint, so a
 * sixteen-bone tail still curls through a full circle.
 */
const TAIL = limits(30, -5, 5);

const UP = unit(0, 1, 0);
const DOWN = unit(0, -1, 0);
const FWD = unit(0, 0, 1);
const LEFT_X = unit(1, 0, 0);
const BACK = unit(0, 0, -1);

/** How many tail slots the hub carries (ADR-031 D4: the count band's ceiling). */
export const TAIL_SLOT_COUNT = 16;
/** The tail slots, root → tip: `Tail1` hangs from the pelvis. */
export const TAIL_SLOT_IDS: readonly string[] = Array.from({ length: TAIL_SLOT_COUNT }, (_, i) => `Tail${i + 1}`);

/**
 * The tail chain (3.4.0): centre bones under Hips pointing BACK, the
 * character's UP as the reference — a tail's rest direction is per-character
 * anatomy, so these are the hub's zero pose only. Every slot articulates:
 * a tail has no tip bone, its last bone is a leaf that still bends.
 */
function tailBones(): OntologyBone[] {
  return TAIL_SLOT_IDS.map((id, i) =>
    centerBone({
      id,
      parent: i === 0 ? 'Hips' : TAIL_SLOT_IDS[i - 1]!,
      region: 'tail',
      jointType: 'ball',
      jointLimits: TAIL,
      dir: BACK,
      sec: UP,
      pc: TAIL_SLOT_IDS[i + 1] ?? null,
    }),
  );
}

/** A soft-tissue leaf's swing: the mass sways about its root, it never twists. */
const SOFT_TISSUE = limits(25, -5, 5);

/**
 * The soft-tissue leaves (3.5.0): one bone per region, pointing from the
 * tissue's root on the body wall to its apex, and a tip. The spring declares
 * the bone and its tip; the tip carries no weight.
 */
export const SOFT_TISSUE_SLOT_IDS = [
  'LeftBreastJiggle',
  'RightBreastJiggle',
  'BellyJiggle',
  'LeftGluteJiggle',
  'RightGluteJiggle',
  'HairJiggle',
] as const;

function softTissueBones(): OntologyBone[] {
  const leaf = (id: string, parent: string, dir: Vec3, side: 'left' | 'center'): OntologyBone[] => {
    const make = side === 'left' ? leftBone : centerBone;
    return [
      make({ id, parent, region: 'softTissue', jointType: 'ball', jointLimits: SOFT_TISSUE, dir, sec: UP, pc: `${id}_End` }),
      make({ id: `${id}_End`, parent: id, region: 'softTissue', tip: true }),
    ];
  };
  const left = [...leaf('LeftBreastJiggle', 'Spine2', FWD, 'left'), ...leaf('LeftGluteJiggle', 'Hips', BACK, 'left')];
  // ADR-039 T2a (3.6.0): the hair's leaf hangs from the Head and points from the skull's centre into the hair.
  return [...leaf('BellyJiggle', 'Spine', FWD, 'center'), ...left, ...left.map(mirrorToRight), ...leaf('HairJiggle', 'Head', BACK, 'center')];
}

function centerBones(): OntologyBone[] {
  // Secondaries (§11): the spine chain carries the character's facing (FWD);
  // the jaw's secondary is the in-plane "front-up" perpendicular of its
  // oblique direction.
  return [
    centerBone({ id: 'Hips', parent: null, region: 'spine', dir: UP, sec: FWD, pc: 'Spine' }),
    centerBone({ id: 'Spine', parent: 'Hips', region: 'spine', jointType: 'ball', jointLimits: SPINE, dir: UP, sec: FWD, pc: 'Spine1' }),
    centerBone({ id: 'Spine1', parent: 'Spine', region: 'spine', jointType: 'ball', jointLimits: SPINE, dir: UP, sec: FWD, pc: 'Spine2' }),
    centerBone({ id: 'Spine2', parent: 'Spine1', region: 'spine', jointType: 'ball', jointLimits: SPINE, dir: UP, sec: FWD, pc: 'Neck' }),
    centerBone({ id: 'Neck', parent: 'Spine2', region: 'spine', jointType: 'ball', jointLimits: NECK, dir: UP, sec: FWD, pc: 'Head' }),
    centerBone({ id: 'Head', parent: 'Neck', region: 'head', jointType: 'ball', jointLimits: HEAD, dir: UP, sec: FWD, pc: 'HeadTop_End' }),
    centerBone({ id: 'HeadTop_End', parent: 'Head', region: 'head', tip: true }),
    centerBone({ id: 'Jaw', parent: 'Head', region: 'head', jointType: 'hinge', jointLimits: JAW, dir: unit(0, -1, 3), sec: unit(0, 3, 1) }),
  ];
}

function leftFingerBones(): OntologyBone[] {
  const fingers: Array<{ name: string; dir: Vec3; sec: Vec3; keepsRest: boolean; mcp: SwingTwistLimits }> = [
    // The thumb is OPPOSED (3.3.0, ADR-019 §2.2): its nail faces the radial
    // side — the in-plane perpendicular of its (1,0,1) direction — not UP.
    // Measured on Mixamo's own rig (Fist Fight B): the thumb MCP flexes about
    // the palm normal (flex·palmNormal −0.95/−0.91 L/R), which puts the nail
    // radial (0.82/0.72). Declared UP, the source thumb's curl axis mapped
    // 90° off the target's and the rest roll landed on the metacarpal.
    // The whole chain keeps its REST under transport (rest anchor, children
    // riding): a thumb's rest roll AND its rest abduction are the rig's
    // convention — Mixamo's T-pose thumb is splayed, a generated character's
    // is relaxed — and plain transport moved Jay's knuckle 47–65 mm at
    // source-rest. Fingers stay plain absolute: a flat hand SHOULD flatten
    // relaxed fingers (field-accepted).
    { name: 'Thumb', dir: unit(1, 0, 1), sec: unit(-1, 0, 1), keepsRest: true, mcp: THUMB_MCP },
    { name: 'Index', dir: LEFT_X, sec: UP, keepsRest: false, mcp: FINGER_MCP },
    { name: 'Middle', dir: LEFT_X, sec: UP, keepsRest: false, mcp: FINGER_MCP },
    { name: 'Ring', dir: LEFT_X, sec: UP, keepsRest: false, mcp: FINGER_MCP },
    { name: 'Pinky', dir: LEFT_X, sec: UP, keepsRest: false, mcp: FINGER_MCP },
  ];
  const bones: OntologyBone[] = [];
  for (const f of fingers) {
    const base = `LeftHand${f.name}`;
    // Palms-down T-pose (spec): every FINGER's dorsal (nail) side faces UP —
    // perpendicular to the ±X fingers. The thumb's is declared above.
    const k = f.keepsRest;
    bones.push(
      leftBone({ id: `${base}1`, parent: 'LeftHand', region: 'handL', jointType: 'ball', jointLimits: f.mcp, dir: f.dir, sec: f.sec, restAnchored: k, childrenRide: k, pc: `${base}2` }),
      leftBone({ id: `${base}2`, parent: `${base}1`, region: 'handL', jointType: 'hinge', jointLimits: FINGER_PIP, dir: f.dir, sec: f.sec, restAnchored: k, childrenRide: k, pc: `${base}3` }),
      leftBone({ id: `${base}3`, parent: `${base}2`, region: 'handL', jointType: 'hinge', jointLimits: FINGER_DIP, dir: f.dir, sec: f.sec, restAnchored: k, childrenRide: k, pc: `${base}4` }),
      leftBone({ id: `${base}4`, parent: `${base}3`, region: 'handL', tip: true }),
    );
  }
  return bones;
}

function leftSideBones(): OntologyBone[] {
  // Secondaries (§11): the palms-down arm chain's dorsal side faces UP; the
  // legs' secondary is the kneecap direction (FWD); the foot's is the
  // in-plane dorsal perpendicular of its oblique direction. The clavicle is
  // the declared childDirectionInformative=false class — its child (the
  // upper arm) points where the ARM goes, not where the clavicle rolls.
  return [
    leftBone({ id: 'LeftEye', parent: 'Head', region: 'head', jointType: 'ball', jointLimits: EYE, dir: FWD, sec: UP }),
    leftBone({ id: 'LeftBreast', parent: 'Spine2', region: 'breast', jointType: 'ball', jointLimits: BREAST, dir: FWD, sec: UP, pc: 'LeftBreast_End' }),
    leftBone({ id: 'LeftBreast_End', parent: 'LeftBreast', region: 'breast', tip: true }),
    leftBone({ id: 'LeftShoulder', parent: 'Spine2', region: 'armL', jointType: 'ball', jointLimits: CLAVICLE, dir: LEFT_X, sec: UP, childInformative: false, restAnchored: true, pc: 'LeftArm' }),
    leftBone({ id: 'LeftArm', parent: 'LeftShoulder', region: 'armL', jointType: 'ball', jointLimits: UPPER_ARM, dir: LEFT_X, sec: UP, pc: 'LeftForeArm' }),
    leftBone({ id: 'LeftForeArm', parent: 'LeftArm', region: 'armL', jointType: 'hinge', jointLimits: ELBOW, dir: LEFT_X, sec: UP, pc: 'LeftHand' }),
    // The hand's direction runs along the middle finger — the canonical child.
    leftBone({ id: 'LeftHand', parent: 'LeftForeArm', region: 'handL', jointType: 'universal', jointLimits: null, dir: LEFT_X, sec: UP, pc: 'LeftHandMiddle1' }),
    ...leftFingerBones(),
    leftBone({ id: 'LeftUpLeg', parent: 'Hips', region: 'legL', jointType: 'ball', jointLimits: UPPER_LEG, dir: DOWN, sec: FWD, pc: 'LeftLeg' }),
    leftBone({ id: 'LeftLeg', parent: 'LeftUpLeg', region: 'legL', jointType: 'hinge', jointLimits: KNEE, dir: DOWN, sec: FWD, pc: 'LeftFoot' }),
    leftBone({ id: 'LeftFoot', parent: 'LeftLeg', region: 'legL', jointType: 'universal', jointLimits: null, dir: unit(0, -1, 2), sec: unit(0, 2, 1), pc: 'LeftToeBase' }),
    leftBone({ id: 'LeftToeBase', parent: 'LeftFoot', region: 'legL', jointType: 'universal', jointLimits: null, dir: FWD, sec: UP, pc: 'LeftToe_End' }),
    leftBone({ id: 'LeftToe_End', parent: 'LeftToeBase', region: 'legL', tip: true }),
  ];
}

function buildBones(): OntologyBone[] {
  const left = leftSideBones();
  return [...centerBones(), ...left, ...left.map(mirrorToRight), ...tailBones(), ...softTissueBones()];
}

const MINIMAL_PROFILE = [
  'Hips',
  'Spine',
  'Spine1',
  'Spine2',
  'Neck',
  'Head',
  'HeadTop_End',
  ...['Left', 'Right'].flatMap((s) => [
    `${s}Shoulder`,
    `${s}Arm`,
    `${s}ForeArm`,
    `${s}Hand`,
    `${s}UpLeg`,
    `${s}Leg`,
    `${s}Foot`,
    `${s}ToeBase`,
    `${s}Toe_End`,
  ]),
];

/**
 * Every finger bone, DERIVED from the ontology's own bones rather than listed —
 * a hand-written list is a second truth that drifts the day a digit is added.
 * The 4th bone of each digit is a tip: it is a member (profile closure requires
 * a bone's parent, and coverage wants the geometry) but it carries no track.
 */
function fingerIds(bones: readonly OntologyBone[]): string[] {
  return bones.map((b) => b.id).filter((id) => /Hand(Thumb|Index|Middle|Ring|Pinky)[1-4]$/.test(id));
}

/** The BBSA ontology, v3.6.0 — plain data, validated by loadOntology at use. */
export function bbsaOntologyData(): OntologyData {
  const bones = buildBones();
  return {
    // 3.1.0 (ADR-003 §11): additive — mirrorPlane, per-bone zeroPoseSecondary,
    // parityAxis, childDirectionInformative. 3.0.0 mappings remain valid under
    // the same-major compatibility rule.
    // 3.2.0 (ADR-019 amendment): additive — restAnchoredTransport, true on the
    // clavicle class (rest direction is anatomy, not pose — chest-collapse
    // forensics 2026-08-09). Schema default false keeps 3.1.0 data valid.
    // 3.3.0 (ADR-019 §2.2): additive — twistAnchoredTransport (declared by
    // no bone today) and anchorChildrenRide; the thumb chain rest-anchored
    // with riding children; the thumb's secondary re-declared radial
    // (opposed). Schema defaults false keep 3.2.0 data valid.
    // 3.4.0 (ADR-031 D6, T2): additive — sixteen optional Tail slots and the
    // 'tail' region; no profile names them, so coverage is unchanged.
    // 3.5.0 (ADR-038 J2): additive: five optional soft-tissue leaves + tips
    // and the 'softTissue' region; no profile names them either.
    // 3.6.0 (ADR-039 T2a): additive: HairJiggle + tip under Head, 'softTissue'.
    version: '3.6.0',
    mirrorPlane: { normal: LEFT_X, offset: 0 },
    profiles: {
      minimal: MINIMAL_PROFILE,
      /**
       * `minimal` plus the ten digits — the profile the milestone needs, and the
       * one that did not exist. MEASURED gap: `minimal` carries no fingers, so a
       * Mixamo clip's 30 finger channels drop as unmapped even when BOTH rigs
       * have fingers; `full` demands Jaw/Eyes/Breasts, which no Mixamo skeleton
       * carries, so it refuses the source outright. Nothing sat in between.
       *
       * With this profile, on a VRoid target and a Mixamo source: 30 finger
       * bones map on each side, 30 finger tracks land, 0 dropped
       * (app/src/import/vrm-fingers-probe.test.ts).
       *
       * Coverage is REQUIRED, so this profile REFUSES a fingerless rig — which
       * is correct and is why a caller must choose the richest profile a rig can
       * actually cover rather than asking for this one by default.
       */
      'minimal+hands': [...MINIMAL_PROFILE, ...fingerIds(bones)],
      // The 72 hub bones. The optional tail slots are not something a rig
      // must HAVE to be complete — they are grafted where a zone asks.
      full: bones.filter((b) => b.region !== 'tail' && b.region !== 'softTissue').map((b) => b.id),
    },
    bones,
  };
}
